/* Входы формы сервера ↔ состояние экрана шаблона «Текст» (план вехи 3, text-cover §2.2 «Local modules», шаг 2 экрана).
   Чистые функции без DOM: какой инструмент шаблона у формы (TOOL_OF_MODE), списки шаблона из формы (выборы «Ещё настройки», «Для чего»,
   шаблоны промпта, пределы полей), вид входов для S (readText) и обратно (writeText). В заказ уходят только ключи опубликованной формы
   (textKeys родной формы): неопубликованный ключ не пишется, а его прежнее значение во входах не трогается.
   Значения — как у родной формы (TextOrderForm): переключатели — 'true' / 'false', шаблоны промпта — JSON подписей сервера (toggleAction:
   «Подробнее» и «Короче» исключают друг друга), «Свой вариант» — поле wishes (выключен — ключа нет), ссылка — только верная (youtubeId),
   «Название и преимущества» — text или name + features (splitProduct), начало фрагмента ролика — from_ms в миллисекундах. */
import type { StudioForm } from '../studio/studio-api';
import { toolDefault, toolField, type ToolField } from '../studio/circle/tool-form';
import {
  choiceLabels,
  selectedActions,
  splitProduct,
  textKeys,
  textLimits,
  textPolicy,
  wishesLimit,
  youtubeId,
  type TextKeys,
  type TextMode,
  type TextPolicy,
} from './native/text-model';

/** Инструмент экрана шаблона (S.tool): yt — тот же экран stt со ссылкой */
export type TextTool = 'stt' | 'seo' | 'pphoto' | 'pvideo' | 'pnew' | 'pfix';
export const TOOL_OF_MODE: Readonly<Record<TextMode, TextTool>> = Object.freeze({
  transcription: 'stt',
  product_description: 'seo',
  prompt_from_photo: 'pphoto',
  prompt_from_video: 'pvideo',
  prompt_create: 'pnew',
  improve: 'pfix',
});
/** Режим сервера по ключу инструмента шаблона (вход studio: tool | mode; yt — расшифровка со ссылкой) */
export const MODE_OF_TOOL: Readonly<Record<string, TextMode>> = Object.freeze({
  stt: 'transcription',
  yt: 'transcription',
  seo: 'product_description',
  pphoto: 'prompt_from_photo',
  pvideo: 'prompt_from_video',
  pnew: 'prompt_create',
  pfix: 'improve',
});
export const inlineTool = (tool: TextTool | null | undefined) => tool === 'pnew' || tool === 'pfix';

/** Форма → инструмент шаблона; форма не «Текста» (нет политики transcription-v1 / text-n8n-v1) — null */
export function toolOf(form: StudioForm | null | undefined): TextTool | null {
  const policy = form ? textPolicy(form) : null;
  return policy ? TOOL_OF_MODE[policy.mode] : null;
}
/** Инструмент по приложению до формы: entry.tool приложения (yt — stt) */
export function toolOfEntry(entry: unknown): TextTool | null {
  const tool = entry && typeof entry === 'object' ? (entry as Record<string, unknown>).tool : undefined;
  const mode = typeof tool === 'string' ? MODE_OF_TOOL[tool] : undefined;
  return mode ? TOOL_OF_MODE[mode] : null;
}

/* ---------- вид входов ---------- */

export interface SttView {
  lang: string;
  spk: boolean;
  tc: boolean;
  /** Верная ссылка из входов ('' — нет) */
  url: string;
  ytKind: string;
  ytLang: string;
}
export interface SeoView {
  /** «Название и преимущества» целиком (первая строка — название) */
  text: string;
  tone: string;
  market: string;
  wish: string;
}
export interface MediaView {
  style: string;
  lang: string;
  /** Начало фрагмента ролика, с (from_ms / 1000) */
  from: number;
}
export interface PromptView {
  text: string;
  target: string;
  tpl: string[];
  own: boolean;
  ownText: string;
}
export type TextView =
  | { tool: 'stt'; v: SttView }
  | { tool: 'seo'; v: SeoView }
  | { tool: 'pphoto' | 'pvideo'; v: MediaView }
  | { tool: 'pnew' | 'pfix'; v: PromptView };

type Inputs = Record<string, string>;
const valuesOf = (form: StudioForm, key: string | undefined) => (key ? (toolField(form, key)?.values ?? []) : []);
/** Значение выбора: из входов, если поле его знает; иначе значение формы по умолчанию; иначе fallback */
function choice(form: StudioForm, key: string | undefined, inputs: Inputs, fallback: string): string {
  if (!key) return fallback;
  const field = toolField(form, key);
  const values = field?.values ?? [];
  const raw = inputs[key];
  if (raw !== undefined && (!values.length || values.includes(raw))) return raw;
  return toolDefault(field) ?? (values.length && !values.includes(fallback) ? values[0] : fallback);
}
/** Переключатель (enum true/false): опубликован, если поле есть и его значения — только true и false (или без значений) */
export function toggleField(form: StudioForm | null | undefined, key: string | undefined): ToolField | null {
  const field = key ? toolField(form, key) : undefined;
  if (!field) return null;
  return !field.values.length || (field.values.includes('true') && field.values.includes('false')) ? field : null;
}
const flag = (form: StudioForm, key: string | undefined, inputs: Inputs, fallback: boolean) => {
  const field = toggleField(form, key);
  if (!field || !key) return fallback;
  const raw = inputs[key] ?? toolDefault(field);
  return raw === undefined ? fallback : raw === 'true';
};

/** Входы → вид экрана инструмента формы (null — форма не «Текста») */
export function readText(form: StudioForm, inputs: Inputs): TextView | null {
  const tool = toolOf(form);
  if (!tool) return null;
  const keys = textKeys(form);
  switch (tool) {
    case 'stt': {
      const url = keys.url ? (inputs[keys.url] ?? '').trim() : '';
      return {
        tool,
        v: {
          lang: choice(form, keys.language, inputs, 'auto'),
          spk: flag(form, keys.diarization, inputs, true),
          tc: flag(form, keys.timestamps, inputs, true),
          url: youtubeId(url) ? url : '',
          ytKind: choice(form, keys.ytKind, inputs, 'full'),
          ytLang: choice(form, keys.ytLang, inputs, 'src'),
        },
      };
    }
    case 'seo':
      return {
        tool,
        v: {
          text: productText(keys, inputs),
          tone: choice(form, keys.tone, inputs, 'sell'),
          market: choice(form, keys.market, inputs, ''),
          wish: keys.wishes ? (inputs[keys.wishes] ?? '') : '',
        },
      };
    case 'pphoto':
    case 'pvideo': {
      const ms = Number(keys.from ? inputs[keys.from] : undefined);
      return {
        tool,
        v: {
          style: choice(form, keys.style, inputs, 'detail'),
          lang: choice(form, keys.lang, inputs, 'ru'),
          from: Number.isFinite(ms) && ms > 0 ? ms / 1000 : 0,
        },
      };
    }
    default: {
      const values = valuesOf(form, keys.kind);
      const target = keys.kind && inputs[keys.kind] && (!values.length || values.includes(inputs[keys.kind])) ? inputs[keys.kind] : (toolDefault(toolField(form, keys.kind ?? '')) ?? values[0] ?? 'photo');
      const own = keys.wishes ? (inputs[keys.wishes] ?? '') : '';
      return {
        tool,
        v: { text: keys.text ? (inputs[keys.text] ?? '') : '', target, tpl: selectedActions(keys.actions ? inputs[keys.actions] : undefined), own: !!own, ownText: own },
      };
    }
  }
}
/** «Название и преимущества» из входов: text, иначе name и features строками */
export function productText(keys: TextKeys, inputs: Inputs): string {
  if (keys.text) return inputs[keys.text] ?? '';
  const name = keys.name ? (inputs[keys.name] ?? '') : '';
  const features = keys.features ? (inputs[keys.features] ?? '') : '';
  return [name, features].filter(Boolean).join('\n');
}

/** Записать ключ: null или '' — ключа нет; неопубликованный ключ (undefined) не трогается */
function put(next: Inputs, key: string | undefined, value: string | null) {
  if (!key) return;
  if (value === null || value === '') delete next[key];
  else next[key] = value;
}
const setChoice = (form: StudioForm, next: Inputs, key: string | undefined, value: string) => {
  if (!key) return;
  const values = valuesOf(form, key);
  if (!values.length || values.includes(value)) put(next, key, value);
};

/** Вид экрана → входы поверх prev (только опубликованные ключи; файлы и их ключи пишет мост загрузки) */
export function writeText(form: StudioForm, view: TextView, prev: Inputs): Inputs {
  /* форма другого инструмента (или без политики «Текста»): писать нечего */
  if (toolOf(form) !== view.tool) return prev;
  const keys = textKeys(form);
  const next = { ...prev };
  switch (view.tool) {
    case 'stt': {
      const v = view.v;
      setChoice(form, next, keys.language, v.lang);
      if (toggleField(form, keys.diarization)) put(next, keys.diarization, v.spk ? 'true' : 'false');
      if (toggleField(form, keys.timestamps)) put(next, keys.timestamps, v.tc ? 'true' : 'false');
      /* ссылка уходит только верная; неверную форма показывает, но в заказ она не идёт (TextSttSource) */
      if (keys.url) put(next, keys.url, youtubeId(v.url) ? v.url.trim() : null);
      setChoice(form, next, keys.ytKind, v.ytKind);
      setChoice(form, next, keys.ytLang, v.ytLang);
      return next;
    }
    case 'seo': {
      const v = view.v;
      const policy = textPolicy(form)!;
      const { max } = textLimits(policy, toolField(form, keys.text || keys.features || ''), 'seo');
      if (keys.text) put(next, keys.text, cut(v.text, max));
      else {
        const { name, features } = splitProduct(cut(v.text, max));
        put(next, keys.name, name);
        put(next, keys.features, features);
      }
      if (v.tone) setChoice(form, next, keys.tone, v.tone);
      if (v.market) setChoice(form, next, keys.market, v.market);
      if (keys.wishes) put(next, keys.wishes, cut(v.wish, wishesLimit(policy, toolField(form, keys.wishes), 300)));
      return next;
    }
    case 'pphoto':
    case 'pvideo': {
      const v = view.v;
      setChoice(form, next, keys.style, v.style);
      setChoice(form, next, keys.lang, v.lang);
      return next;
    }
    default: {
      const v = view.v;
      const policy = textPolicy(form)!;
      if (keys.text) put(next, keys.text, v.text);
      setChoice(form, next, keys.kind, v.target);
      if (keys.actions) {
        const offered = templatesOf(policy, v.target);
        const tpl = v.tpl.filter(label => offered.includes(label));
        put(next, keys.actions, tpl.length ? JSON.stringify(tpl) : null);
      }
      if (keys.wishes) put(next, keys.wishes, v.own && v.ownText ? cut(v.ownText, wishesLimit(policy, toolField(form, keys.wishes))) : null);
      return next;
    }
  }
}
/** Начало фрагмента ролика во входах (from_ms): с округлением до миллисекунд; поля нет — prev */
export function writeFrom(form: StudioForm, from: number | null, prev: Inputs): Inputs {
  const keys = textKeys(form);
  if (!keys.from) return prev;
  const next = { ...prev };
  put(next, keys.from, from === null ? null : String(Math.max(0, Math.round(from * 1000))));
  return next;
}
const cut = (value: string, max: number) => Array.from(value).slice(0, max).join('');

/** Шаблоны промпта сервера для назначения (constraints.text.actions[target]: подписи) */
export function templatesOf(policy: TextPolicy | null | undefined, target: string): string[] {
  const actions = policy?.actions;
  const list = actions && typeof actions === 'object' ? (actions as Record<string, unknown>)[target] : undefined;
  return Array.isArray(list) ? list.filter((item): item is string => typeof item === 'string' && !!item && item.length <= 120) : [];
}

/* ---------- списки шаблона из формы ---------- */

/** Списки экрана шаблона, которые форма наполняет на месте (engine.ts: OPT, TPL, TPL_X, FOR, BOX) */
export interface TextLists {
  OPT: Record<string, string[][]>;
  TPL: Record<string, Record<string, string[][]>>;
  TPL_X: Record<string, string>;
  FOR: string[][];
  BOX: Record<string, { min: number; max: number }>;
}
/** Копия списков шаблона (подписи значений, слова назначения): форма их только фильтрует и дополняет */
export interface TextListsBase {
  OPT: Record<string, string[][]>;
  FOR: string[][];
}
export function listsBase(l: Pick<TextLists, 'OPT' | 'FOR'>): TextListsBase {
  return { OPT: Object.fromEntries(Object.entries(l.OPT).map(([k, v]) => [k, v.map(o => o.slice())])), FOR: l.FOR.map(f => f.slice()) };
}
/** Пункты выбора по значениям поля: подпись шаблона, иначе подпись родной формы (choiceLabels), иначе само значение */
function options(values: string[], base: string[][] | undefined): string[][] {
  return values.map(value => [value, base?.find(o => o[0] === value)?.[1] ?? choiceLabels[value] ?? value]);
}
const fill = (list: string[][], items: string[][]) => list.splice(0, list.length, ...items);

/** Форма → списки шаблона на месте (base — копия списков шаблона до первой формы) */
export function fillTextLists(form: StudioForm, L: TextLists, base: TextListsBase): void {
  const policy = textPolicy(form);
  const tool = toolOf(form);
  if (!policy || !tool) return;
  const keys = textKeys(form);
  const vals = (key: string | undefined) => valuesOf(form, key);
  if (tool === 'stt') {
    if (vals(keys.language).length) fill(L.OPT.lang, options(vals(keys.language), base.OPT.lang));
    if (vals(keys.ytKind).length) fill(L.OPT.ytkind, options(vals(keys.ytKind), base.OPT.ytkind));
    if (vals(keys.ytLang).length) fill(L.OPT.ytlang, options(vals(keys.ytLang), base.OPT.ytlang));
  }
  if (tool === 'seo') {
    if (vals(keys.market).length) fill(L.OPT.market, options(vals(keys.market), base.OPT.market));
    if (vals(keys.tone).length) fill(L.OPT.tone, options(vals(keys.tone), base.OPT.tone));
    const { max } = textLimits(policy, toolField(form, keys.text || keys.features || ''), 'seo');
    L.BOX.feat.max = max;
  }
  if (tool === 'pphoto' || tool === 'pvideo') {
    if (vals(keys.style).length) fill(L.OPT.pstyle, options(vals(keys.style), base.OPT.pstyle));
    if (vals(keys.lang).length) fill(L.OPT.plang, options(vals(keys.lang), base.OPT.plang));
  }
  if (inlineTool(tool)) {
    const box = tool === 'pnew' ? L.BOX.idea : L.BOX.fix;
    const { min, max } = textLimits(policy, toolField(form, keys.text ?? ''), tool);
    box.min = min;
    box.max = max;
    /* «Для чего»: значения поля kind (слово «для фото» — шаблона) */
    const targets = vals(keys.kind).length ? vals(keys.kind) : base.FOR.map(f => f[0]);
    fill(L.FOR, targets.map(value => base.FOR.find(f => f[0] === value)?.slice() ?? [value, choiceLabels[value] ?? value, (choiceLabels[value] ?? value).toLowerCase()]));
    /* шаблоны — подписи сервера (ключ и подпись — одно), у каждого назначения формы свой ряд */
    const own = L.TPL[tool] ?? (L.TPL[tool] = {});
    for (const key of Object.keys(own)) delete own[key];
    for (const [target] of L.FOR) own[target] = templatesOf(policy, target).map(label => [label, label]);
    for (const key of Object.keys(L.TPL_X)) delete L.TPL_X[key];
    Object.assign(L.TPL_X, { Подробнее: 'Короче', Короче: 'Подробнее' });
  }
}
